import * as React from 'react'
import {Lang} from 'yougile-lang'
import {Time, Util} from 'yougile-util'
import {baseStandardPriceRub, standardDiscountsPercent} from 'yougile-model'
import {TrackingEvent} from '@common/tracking-events'
import {Tracking} from '../../../common/tracking'
import {setDisplayName} from '../../../common/utils-ui'
import {BillingProps, calcTotal} from './billing-base'

export const BillingBuy = (props: BillingProps) => {
  const {license, onChange, paymentStatus} = props
  const type = license.type
  if (!type) {
    return null
  }

  const bem = Util.bemFrom('billing')
  const total = calcTotal(props)
  const users = type === 'prolong' ? paymentStatus.users : license.users
  const perUser =
    users && license.months ? Math.ceil(total / users / license.months) : 0
  const monthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    void Tracking.send(TrackingEvent.billingChangeLicenseClick)
    onChange({...license, months: +e.target.value as any})
  }
  const usersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    void Tracking.send(TrackingEvent.billingChangeLicenseClick)
    onChange({
      ...license,
      users:
        e.target.value === '' ? 0 : Math.max(Math.floor(+e.target.value), 0)
    })
  }
  const discount = standardDiscountsPercent[license.months] || 0
  const showDiscount =
    (type === 'buy' || type === 'prolong') &&
    discount > 0 &&
    total > 0 &&
    perUser < baseStandardPriceRub
  const title = {
    buy: Lang.msg.bill.buyLicense,
    prolong: Lang.msg.billingProlongTitle,
    'add-users': Lang.msg.billingAddUsersTitle
  }[type]

  return (
    <div className={bem('section')}>
      <div className={bem('large')}>{title}</div>
      <div className={bem('chooser', {[type]: true})}>
        {type !== 'add-users' && (
          <select
            className={bem('chooser', 'duration')}
            value={license.months}
            onChange={monthChange}
            onKeyDown={onKeyDown}
          >
            <option value={24}>{Lang.msg.billingDuration24}</option>
            <option value={12}>{Lang.msg.billingDuration12}</option>
            <option value={6}>{Lang.msg.billingDuration6}</option>
            <option value={3}>{Lang.msg.billingDuration3}</option>
            <option value={1}>{Lang.msg.billingDuration1}</option>
          </select>
        )}
        {type !== 'prolong' && (
          <>
            <div className={bem('chooser', 'users-hint')}>
              {Lang.msg.billingNumberOfUsers}:
            </div>
            <input
              value={users || ''}
              type="number"
              onChange={usersChange}
              className={bem('chooser', 'users-count')}
              onKeyDown={onKeyDown}
            />
          </>
        )}

        {type === 'add-users' && (
          <div className={bem('users-total-hint')}>
            {Lang.msg.billingTotalUsers}:{' '}
            {paymentStatus.users + (license.users || 0) + 10}
          </div>
        )}

        {type === 'prolong' && (
          <div className={bem('license-exp-hint')}>
            {Lang.msg.billingLicenseTill}:{' '}
            {Time.formatDate(
              Time.addDuration(paymentStatus.expiration, {
                unit: 'm',
                amount: license.months
              }),
              {alwaysWithYear: true}
            )}
          </div>
        )}

        {type !== 'add-users' ? (
          <div className={bem('calc')}>
            <div className={bem('calc', 'descr')}>
              {Lang.msg.billingPerUser}
            </div>
            <div className={bem('calc', 'num')}>
              {perUser}{' '}
              <div className={bem('calc', 'text')}>
                {Lang.msg.rub} {Lang.msg.billingPerMonth}
              </div>
              {showDiscount && (
                <div className={bem('calc', 'discount')}>
                  <div className={bem('calc', 'old-price')}>
                    {baseStandardPriceRub} {Lang.msg.rub2}
                  </div>{' '}
                  (-{discount}%)
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={bem('calc')} />
        )}

        <div className={bem('calc', {second: true})}>
          <div className={bem('calc', 'descr')}>
            {type === 'add-users'
              ? Lang.msg.billingTotal
              : Lang.msg.bill.forUsers + ' ' + Lang.msg.userCount(users)}
          </div>
          <div className={bem('calc', 'num')}>
            {total || '—'}{' '}
            <div className={bem('calc', 'text')}>{Lang.msg.rub}</div>
            {showDiscount && (
              <div className={bem('calc', 'discount')}>
                <div className={bem('calc', 'old-price')}>
                  {baseStandardPriceRub * license.months * users}{' '}
                  {Lang.msg.rub2}
                </div>{' '}
                (-{discount}%)
              </div>
            )}
          </div>
        </div>
      </div>

      {type === 'buy' && (
        <div className={bem('users-result')}>
          {Lang.msg.bill.usersResult}: <b>{users + 10}</b>
        </div>
      )}
    </div>
  )
}

setDisplayName(BillingBuy, 'BillingBuy')

const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
  if (e.code === 'Escape') {
    const el = e.target as HTMLElement
    el.blur()
    e.stopPropagation()
  }
}
